<template>
  <div ref="container">
    <div
      v-if="!parsedEvents.length"
      class="p-l-10 p-t-5 p-b-5 p-r-5"
    >
      <i>No events</i>
    </div>

    <div
      v-for="(event, index) in parsedEvents"
      :key="index"
      class="timeline__item-container"
    >
      <div class="timeline__item">
        <div
          class="timeline__circle"
          :class="{
            'bg-info white': infoEvents[event.name.toLowerCase()],
            'bg-success white': successEvents[event.name.toLowerCase()],
            'bg-warn white': warnEvents[event.name.toLowerCase()],
            'bg-danger white': errorEvents[event.name.toLowerCase()],
          }"
        >
          <i
            class="fa"
            :class="{
              'fa-info': infoEvents[event.name.toLowerCase()],
              'fa-check': successEvents[event.name.toLowerCase()],
              'fa-flag': warnEvents[event.name.toLowerCase()],
              'fa-exclamation': errorEvents[event.name.toLowerCase()],
            }"
          />
        </div>
  
        <div>
          <div class="m-b-3">
            <strong class="f-s-13">{{ event.displayName }}</strong>
          </div>
            
          <div :class="{ 'routing-plan-container': event.name === callPlanDetailEventName || event.name === pingTreeSummaryEventNames }">
            <div class="p-l-10 timeline__timestamp">
              <div
                class="generic-args"
              >
                <span
                  class="generic-args__name overflow-ellipsis"
                >
                  Timestamp
                </span>
    
                <span class="generic-args__value">
                  <TruncatedTextCopy
                    :copy-text="event.dateFormatted"
                  >
                    {{ event.dateFormatted }}
                  </TruncatedTextCopy>
                </span>
              </div>
            </div>
            
            <div
              v-for="(column, columnIndex) in event.columns"
              :key="columnIndex"
              class="p-l-10"
            >
              <div
                v-if="!isCustomEventEntry(column.name)"
                class="generic-args"
              >
                <span
                  :title="column.displayName"
                  class="generic-args__name overflow-ellipsis"
                >
                  {{ column.displayName }}
                </span>
    
                <span class="generic-args__value">
                  <TruncatedTextCopy
                    :copy-text="column.formattedValue"
                  >
                    {{ column.formattedValue }}
                  </TruncatedTextCopy>
                </span>
              </div>
  
              <RoutingPlanDetail
                v-if="callPlanDetailEventNames[column.name] && event.name == 'CallPlanDetail'"
                :targets="column.formattedValue"
                :column-name="column.name"
                :column-display-name="column.displayName"
              />

              <RTBRerouteEligibilitySummary
                v-if="rerouteEligibilityDetailEventNames[column.name] && event.name == 'RTBRerouteEligibilitySummary'"
                :targets="column.formattedValue"
                :column-name="column.name"
                :column-display-name="column.displayName"
              />

              <PingTreePinggingSummary 
                v-if="pingTreeSummaryEventNames[column.name]"
                :targets="column.formattedValue"
                :column-name="column.name"
                :column-display-name="column.displayName"
              />

              <RpcCalculationSummary 
                v-if="rpcCalculationSummaryEventNames[column.name]"
                :targets="column.formattedValue"
                :column-name="column.name"
                :column-display-name="column.displayName"
              />

              <TargetHangupRerouteSummary
                v-if="targetHangupRerouteSummary[column.name] && event.name === 'TargetHangupRerouteSummary'"
                :targets="column.formattedValue"
                :column-name="column.name"
                :column-display-name="column.displayName"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';

import eventsMapping from './eventsMapping';
import { eventFieldFormatters } from '../../../../AgGridUtils';
import TruncatedTextCopy from '@/angularBridgeCode/components/TruncatedTextCopy';
import callLogDetailMixin from '../mixins/callLogDetailMixin';
import RoutingPlanDetail from './RoutingPlanDetail';
import RTBRerouteEligibilitySummary from './RTBRerouteEligibilitySummary.vue';
import PingTreePinggingSummary from './PingTreePinggingSummary';
import RpcCalculationSummary from './RpcCalculationSummary';
import TargetHangupRerouteSummary from './TargetHangupRerouteSummary';

const excludeKeys = {
  'dtStamp': true, 
  'id': true,
  name: true,
  eventStrVals: true,
  eventVals: true
};

export default {
  name: 'EventsCallLogDetail',
  components: {
    TruncatedTextCopy,
    RoutingPlanDetail,
    PingTreePinggingSummary,
    RpcCalculationSummary,
    TargetHangupRerouteSummary,
    RTBRerouteEligibilitySummary
  },

  mixins: [callLogDetailMixin],

  props: {
    events: {
      type: Array,
      default() {
        return [];
      },
      required: true
    },

    timeZoneId: {
      type: String,
      default() {
        return moment.tz.guess();
      }
    },

    role: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      infoEvents: Object.freeze({
        'pixelfire': true,
        'callwebhook': true,
        'uploadofflineconversion': true,
        'endcallsource': true,
        'completedcall': true,
        'callrecordingmigrated': true,
        'userinput': true,
        'ivrnodeenter': true,
        'ivrnodeexit': true,
        'splittestselected': true,
        'callplandetail': true,
        'rtbrerouteeligibilitysummary': true,
        'pingrawresult': true,
        'secondpingrawresult': true,
        'dataenrichment': true,
        'blacklistcheck': true,
        'jornayatcpacheck': true,
        'trustedformcheck': true,
        'pingtreepingingsummary': true,
        'secondsteppassthroughpingingsummary': true,
        'rpccalculationsummary': true,
        'transcriptionrequested': true,
        'targethangupreroutesummary': true,
      }),
      successEvents: Object.freeze({
        'targetdialed': true,
        'connectedcall': true,
        'convertedcall': true,
        'payoutcall': true,
        'callwebhook': true,
        'conversionadjustmentapproved': true,
        'uploadofflineconversiongadssuccess': true,
        'pingsuccess': true,
        'secondpingsuccess': true,
        'rtbwinningbid': true,
        'customdataenrichmentsuccess': true,
        'calltranscription': true,
        'parenthanguprerouted': true,
        'childhanguprerouted': true
      }),
      warnEvents: Object.freeze({
        'voidedcall': true,
        'nopayoutcall': true,
        'noconversioncall': true,
        'duplicatecall': true,
        'conversionadjustmentrequested': true,
        'convertedcall_void': true,
        'payoutcall_void': true,
      }),
      errorEvents: Object.freeze({
        'blockedcall': true,
        'rejectedforfunds': true,
        'notargetsanswered': true,
        'notargetfound': true,
        'busycall': true,
        'noanswer': true,
        'errorcall': true,
        'userinputfail': true,
        'recordedcallfail': true,
        'incompletecall': true,
        'conversionadjustmentrejected': true,
        'pingfailure': true,
        'secondpingfailure': true,
        'javascripterror': true,
        'pingtreeprocessfailure': true,
        'uploadofflineconversiongadsfailure': true,
        'customdataenrichmentfailure': true,
        'transcriptionerror': true,
      }),
      callPlanDetailEventNames: Object.freeze({
        'ineligibleTargets': true,
        'eligibleOrderedTargets': true
      }),
      rerouteEligibilityDetailEventNames: Object.freeze({
        'ineligibleTargets': true,
        'eligibleOrderedTargets': true
      }),
      pingTreeSummaryEventNames: Object.freeze({
        'acceptedRingTreeTargets': true,
        'notAcceptedRingTreeTargets': true,
        'winningRingTreeTarget': true
      }),
      rpcCalculationSummaryEventNames: Object.freeze({
        'duplicate': true,
        'errored': true,
        'calculated': true
      }),
      targetHangupRerouteSummary: Object.freeze({
        'acceptedTargets': true,
        'notAcceptedTargets': true
      }),
      parsedEvents: []
    };
  },

  mounted () {
    this.parsedEvents = this.parseEvents(this.events);
  },

  created () {
    this.callPlanDetailEventName = 'CallPlanDetail';
  },

  methods: {
    // Place column names in the below function that are to be overridden by a custom component -- otherwise it will render the generic column as well as the custom column
    isCustomEventEntry(columnName) {
      return this.callPlanDetailEventNames[columnName] || this.pingTreeSummaryEventNames[columnName] || this.rpcCalculationSummaryEventNames[columnName] || this.targetHangupRerouteSummary[columnName] || this.rerouteEligibilityDetailEventNames[columnName];
    },
    parseEvents(events) {
      const parsedEvents = [];
      events.reduce((parsedEvents, event) => {
        const eventMetadata = eventsMapping[event.name];
        let displayName = '';



        if (eventMetadata) {
          const columns = [];
          displayName = eventMetadata['DisplayName'] ? eventMetadata['DisplayName'] : Vue.filter('camelCaseFormat')(event.name);

          if (eventMetadata['DisplayNameForRoles']?.[this.role]) {
            displayName = eventMetadata['DisplayNameForRoles'][this.role];
          }

          event.eventVals?.forEach(eventVal => {
            const fieldMetadata = eventMetadata.Fields[eventVal.name];

            if (eventVal.value !== '') {
              columns.push({
                name: eventVal.name,
                displayName: fieldMetadata?.Field['DisplayName'] || Vue.filter('camelCaseFormat')(eventVal.name),
                formattedValue: (eventFieldFormatters[fieldMetadata?.Field?.FieldType] && eventFieldFormatters[fieldMetadata?.Field?.FieldType](eventVal.value, this.timeZoneId)) 
                              || eventVal.value
              });
            }
          });

          event.eventStrVals?.forEach(eventVal => {
            const fieldMetadata = eventMetadata.Fields[eventVal.name];

            if (eventVal.value !== '') {
              columns.push({
                name: eventVal.name,
                displayName: fieldMetadata?.Field['DisplayName'] || Vue.filter('camelCaseFormat')(eventVal.name),
                formattedValue: (eventFieldFormatters[fieldMetadata?.Field?.FieldType] && eventFieldFormatters[fieldMetadata.Field.FieldType](eventVal.value))
                              || eventVal.value
              });
            }
          });

          for (const key in event) {
            if (!excludeKeys[key] && event[key] !== '') {
              const fieldMetadata = eventMetadata.Fields[key];

              columns.push({
                name: key,
                weight: fieldMetadata?.FieldWeight,
                displayName: fieldMetadata?.Field['DisplayNameForRoles']?.[this.role] || fieldMetadata?.Field['DisplayName'] || Vue.filter('camelCaseFormat')(key),
                formattedValue: (eventFieldFormatters[fieldMetadata?.Field?.FieldType] && eventFieldFormatters[fieldMetadata.Field.FieldType](event[key], this.timeZoneId))
                                || event[key]
              });
            }
          }

          columns.sort((columnA, columnB) => {

            if (columnA.weight && columnB.weight) {
              if(columnA.weight < columnB.weight) {
                return -1; 
              }
              if(columnA.weight > columnB.weight) {
                return 1;
              }
            } else {
              if(columnA.displayName < columnB.displayName) { return -1; }
              if(columnA.displayName > columnB.displayName) { return 1; }
            }
            return 0;
          });

          parsedEvents.push({
            name: event.name,
            displayName,
            dtStamp: event.dtStamp,
            dateFormatted: eventFieldFormatters['date'](event.dtStamp, this.timeZoneId),
            columns
          });
        }

        return parsedEvents;
      }, parsedEvents);

      return parsedEvents.sort((eventA, eventB) => {
        const eventAEpoch = moment(eventA.dtStamp).valueOf();
        const eventBEpoch = moment(eventB.dtStamp).valueOf();

        return eventAEpoch - eventBEpoch;
      });
    }
  },
};
</script>

<style lang="scss">
.timeline__item-container {
  display: flex;
  padding: 10px;

  &:nth-child(odd) {
    background: var(--reset-body-bg-color);
  }

  &:first-of-type {
    margin-top: 1px;
  }

  &:last-of-type {
    .timeline__item {
      &:before {
        display: none;
      }
    }
  }
}

.timeline__item {
  position: relative;
  display: flex;
  
  &:before {
    content: '';
    width: 1px;
    background: var(--component-user-task-actions-history-line-bg);
    position: absolute;
    top: 18px;
    left: 8px;
    z-index: 1;
    bottom: -20px;              
  }
}

.timeline__item--hide-timeline {
  &:before {
    display: none;
  }
}

.timeline__circle {
  width: 17px;
  height: 17px;
  color: var(--component-user-task-actions-history-circle-font-color);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
  z-index: 2;
  font-size: 0.8em;

  &.bg-primary, &.bg-success, &.bg-danger, &.bg-warn {
    &:hover {
      color: var(--component-user-task-actions-history-circle--modified-font-color);
    }
  }
}

.timeline__circle--small {
  width: 14px;
  height: 14px;
}

.timeline__timestamp {
  order: 1;
}

.timeline-popover {
  padding: 20px;
}

.timeline__detail {
  font-size: 0.8em;
}

.generic-args {
  display: flex;
  padding-left: 1px;
  line-height: 20px;
  align-items: center;
}

.generic-args__name {
  flex-shrink: 0;
  flex-basis: 172px;
}

.generic-args__value {
  max-width: 600px;
}

.routing-plan-container {
  display: flex;
  flex-direction: column-reverse;
}
</style>